/**
 * CursoGuardiaSeguridadPrivada - Página de detalle del curso
 * Formación Guardia de Seguridad Privada - Ley 21.659
 * Modalidad: Presencial + Online (material)
 */

import { motion } from 'framer-motion';
import {
    Clock,
    BookOpen,
    Shield,
    Users,
    Award,
    CheckCircle2,
    ArrowRight,
    FileText,
    Heart,
    Swords,
    Building2,
    Radio,
    AlertTriangle,
    Scale,
    Dumbbell,
    MapPin,
    ChevronDown,
    GraduationCap,
    Calendar,
    Laptop,
    ExternalLink
} from 'lucide-react';
import { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const WHATSAPP_URL = `https://wa.me/56975483729?text=${encodeURIComponent('¡Hola! Me interesa inscribirme en el curso de Formación Guardia de Seguridad Privada. ¿Podrían darme más información?')}`;

// Malla curricular del curso
const modulos = [
    { nombre: 'Legislación de Seguridad Privada (Ley 21.659)', horasTeoricas: 22, horasPracticas: 5, icon: Scale },
    { nombre: 'Prevención de Riesgos', horasTeoricas: 5, horasPracticas: 5, icon: AlertTriangle },
    { nombre: 'Control de Emergencias', horasTeoricas: 4, horasPracticas: 0, icon: Shield },
    { nombre: 'Administración de Seguridad Privada', horasTeoricas: 4, horasPracticas: 0, icon: Building2 },
    { nombre: 'Seguridad de Instalaciones', horasTeoricas: 14, horasPracticas: 0, icon: Building2 },
    { nombre: 'Conocimientos de Sistemas de Alarmas', horasTeoricas: 3, horasPracticas: 3, icon: Radio },
    { nombre: 'Sistemas de Comunicación y Enlaces', horasTeoricas: 2, horasPracticas: 2, icon: Radio },
    { nombre: 'Valores y Ética', horasTeoricas: 4, horasPracticas: 0, icon: Heart },
    { nombre: 'Primeros Auxilios', horasTeoricas: 2, horasPracticas: 4, icon: Heart },
    { nombre: 'Defensa Personal', horasTeoricas: 2, horasPracticas: 8, icon: Swords },
    { nombre: 'Instrucción Física', horasTeoricas: 0, horasPracticas: 6, icon: Dumbbell },
];

// Requisitos para chilenos
const requisitosChilenos = [
    'Cédula de identidad vigente por ambos lados',
    'Certificado de antecedentes para fines especiales (vigencia 30 días)',
    'Certificado de estudios: 4to medio aprobado',
    'Certificado médico: Apto para ser Guardia de Seguridad',
    'Ser mayor de 18 años',
];

// Requisitos para extranjeros
const requisitosExtranjeros = [
    'Residencia definitiva (en trámite NO sirve)',
    'Estudios apostillados y validados en Chile (equivalente a 4to medio mínimo)',
    'Certificado de antecedentes para fines especiales (vigencia 30 días)',
    'Certificado médico: Apto para ser Guardia de Seguridad',
];

// FAQ actualizado según Ley 21.659
const faqs = [
    {
        pregunta: '¿Quién emite la credencial de Guardia de Seguridad ahora?',
        respuesta: 'Con la nueva Ley 21.659, es la Subsecretaría de Prevención del Delito (SPD) quien autoriza, emite y controla las credenciales de seguridad privada. Carabineros mantiene solo rol fiscalizador.',
    },
    {
        pregunta: '¿Puedo realizar el curso teniendo DICOM?',
        respuesta: 'Sí, puedes realizar el curso de Guardia de Seguridad teniendo DICOM. No es excluyente para la formación.',
    },
    {
        pregunta: '¿Cuánto tiempo dura el curso?',
        respuesta: 'El curso tiene una duración de 2 semanas, con un total de 90 horas cronológicas. Horario de 09:00 a 18:00 horas.',
    },
    {
        pregunta: '¿Qué vigencia tiene la credencial SPD?',
        respuesta: 'Según la Ley 21.659, las credenciales pueden tener vigencia de hasta 4 años. Antes de cumplirse el período deberás realizar el curso de perfeccionamiento.',
    },
    {
        pregunta: '¿Son válidas las credenciales OS10 anteriores?',
        respuesta: 'Sí. Las autorizaciones otorgadas antes del 28 de noviembre de 2025 seguirán siendo válidas hasta su fecha de vencimiento original.',
    },
    {
        pregunta: '¿Con qué porcentaje se aprueba el examen?',
        respuesta: 'El examen se aprueba con un mínimo del 60%. Si no apruebas, puedes repetir la prueba hasta en tres oportunidades.',
    },
    {
        pregunta: '¿Dónde se rinde el examen final?',
        respuesta: 'El examen se rinde de forma presencial en las dependencias del Instituto de Capacitación Instituto Valparaíso, ubicado en Avenida Colón 2211.',
    },
];

// Componente FAQ Item
function FAQItem({ pregunta, respuesta }) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="border border-white/10 rounded-xl overflow-hidden"
        >
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full p-5 flex items-center justify-between bg-white/5 hover:bg-white/10 transition-colors text-left"
            >
                <span className="font-semibold text-white">{pregunta}</span>
                <ChevronDown className={`w-5 h-5 text-cyan-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    className="p-5 bg-white/5 border-t border-white/10"
                >
                    <p className="text-slate-400">{respuesta}</p>
                </motion.div>
            )}
        </motion.div>
    );
}

export default function CursoGuardiaSeguridadPrivada() {
    const [activeTab, setActiveTab] = useState('chilenos');

    return (
        <div className="min-h-screen bg-slate-900">
            <Header />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img
                        src="/instituteroom.webp"
                        alt="Sala de clases Instituto Valparaíso"
                        className="w-full h-full object-cover opacity-30"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/90 to-slate-900" />
                </div>

                {/* Decorative elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl" />
                    <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl" />
                </div>

                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-4xl"
                    >
                        {/* Badges */}
                        <div className="flex flex-wrap gap-3 mb-6">
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-500/20 text-blue-300 text-sm font-medium rounded-full">
                                <Clock className="w-4 h-4" />
                                90 horas
                            </span>
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-500/20 text-emerald-300 text-sm font-medium rounded-full">
                                <MapPin className="w-4 h-4" />
                                Presencial + Online
                            </span>
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/20 text-amber-300 text-sm font-medium rounded-full">
                                <Award className="w-4 h-4" />
                                Credencial SPD
                            </span>
                        </div>

                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 font-heading leading-tight">
                            Formación{' '}
                            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                                Guardia de Seguridad Privada
                            </span>
                        </h1>

                        <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-3xl">
                            Conviértete en un profesional de la seguridad privada con credencial oficial
                            de la Subsecretaría de Prevención del Delito (SPD). Formación integral que combina teoría y práctica para
                            garantizar tu éxito en el sector.
                        </p>

                        {/* Price and CTA */}
                        <div className="flex flex-wrap items-center gap-6">
                            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 opacity-60">
                                <p className="text-slate-400 text-sm mb-1">Inversión</p>
                                <p className="text-4xl font-bold text-white">$110.000 <span className="text-lg text-slate-400">CLP</span></p>
                            </div>
                            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                                <motion.button
                                    whileHover={{ scale: 1.05, y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 text-white font-bold rounded-xl shadow-lg hover:bg-blue-500 transition-colors"
                                >
                                    Inscribirme Ahora
                                </motion.button>
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Nueva Ley 21.659 Section */}
            <section className="py-12 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-y border-amber-500/20">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="flex flex-col md:flex-row items-start gap-6"
                    >
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-2xl font-bold text-white mb-3">
                                Nueva Ley de Seguridad Privada N° 21.659
                            </h2>
                            <p className="text-slate-300 leading-relaxed mb-4">
                                Desde noviembre de 2025, la formación y acreditación de guardias se rige por esta nueva ley.
                                La <strong className="text-amber-400">Subsecretaría de Prevención del Delito (SPD)</strong> es ahora el órgano rector,
                                mientras que Carabineros mantiene únicamente funciones fiscalizadoras. Las credenciales pueden alcanzar una vigencia de hasta <strong className="text-amber-400">4 años</strong>.
                            </p>
                            <ul className="space-y-2 text-slate-400 mb-4">
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-amber-400" />
                                    Guardias deben contar con chaleco anticorte y seguro de vida
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-amber-400" />
                                    Cámara corporal obligatoria en funciones de alto riesgo
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-amber-400" />
                                    Escolaridad mínima: 4to medio
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 className="w-5 h-5 text-amber-400" />
                                    Credenciales anteriores (OS10) válidas hasta su vencimiento
                                </li>
                            </ul>
                            <a
                                href="https://segprivada.minsegpublica.gob.cl/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-medium transition-colors"
                            >
                                Portal oficial SPD
                                <ExternalLink className="w-4 h-4" />
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Sobre el Curso */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7 }}
                        >
                            <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-600 text-sm font-medium rounded-full mb-6">
                                Sobre el Curso
                            </span>
                            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6 font-heading">
                                Objetivo del Programa
                            </h2>
                            <p className="text-lg text-slate-600 leading-relaxed mb-6">
                                Entregar al alumno conocimientos y herramientas sobre técnicas de seguridad privada,
                                fundamentales para responder con certeza y oportunidad ante eventos relacionados con
                                la seguridad que puedan afectar a los copropietarios de alguna comunidad o institución,
                                y a sus activos.
                            </p>
                            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                                <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                                    <Users className="w-5 h-5 text-blue-500" />
                                    Dirigido a
                                </h3>
                                <p className="text-slate-600">
                                    Todas aquellas personas que cumplan —o aspiren hacerlo— funciones como guardias de
                                    seguridad, nocheros, rondines u otros de similar carácter. También para quienes
                                    realizan control de acceso en condominios habitacionales, comerciales, empresas y eventos.
                                </p>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7 }}
                            className="relative"
                        >
                            <div className="absolute -inset-4 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-3xl -rotate-2" />
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                                <img
                                    src="/credencial_spd.png"
                                    alt="Credencial de Seguridad Privada - Subsecretaría de Prevención del Delito"
                                    className="w-full h-auto"
                                />
                            </div>
                            <div className="absolute -bottom-6 -right-6 bg-white rounded-xl p-4 shadow-xl border border-slate-100">
                                <p className="text-sm text-slate-500 mb-1">Acreditados por</p>
                                <p className="font-bold text-slate-800">Subsecretaría de Prevención del Delito</p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Malla Curricular */}
            <section className="py-20 bg-slate-50">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-600 text-sm font-medium rounded-full mb-4">
                            Programa de Estudios
                        </span>
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 font-heading">
                            Malla Curricular
                        </h2>
                        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                            11 módulos diseñados para formarte integralmente en seguridad privada según la Ley 21.659
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                        {modulos.map((modulo, index) => {
                            const Icon = modulo.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.05 }}
                                    whileHover={{ y: -5 }}
                                    className="p-6 rounded-2xl bg-white shadow-lg border border-slate-100"
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                        <div className="flex-grow">
                                            <h3 className="font-bold text-slate-800 mb-2">{modulo.nombre}</h3>
                                            <div className="flex gap-4 text-sm">
                                                <span className="text-blue-600">
                                                    {modulo.horasTeoricas}h teóricas
                                                </span>
                                                {modulo.horasPracticas > 0 && (
                                                    <span className="text-emerald-600">
                                                        {modulo.horasPracticas}h prácticas
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Total horas */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="mt-12 text-center"
                    >
                        <div className="inline-flex items-center gap-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-8 py-4 rounded-2xl shadow-lg">
                            <Clock className="w-6 h-6" />
                            <span className="font-bold text-lg">Total: 90 horas cronológicas</span>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Nuestras Instalaciones */}
            <section className="py-20 bg-slate-900">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="inline-block px-4 py-1.5 bg-blue-500/20 text-blue-400 text-sm font-medium rounded-full mb-4">
                            Infraestructura
                        </span>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">
                            Nuestras{' '}
                            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                                Instalaciones
                            </span>
                        </h2>
                        <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                            Contamos con espacios diseñados para tu formación teórica y práctica
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="relative group"
                        >
                            <div className="rounded-2xl overflow-hidden">
                                <img
                                    src="/instituteroom.webp"
                                    alt="Sala de clases del Instituto Valparaíso"
                                    className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <h3 className="text-xl font-bold text-white mb-2">Sala de Clases</h3>
                                    <p className="text-slate-300 text-sm">
                                        Aula equipada para clases teóricas con material audiovisual moderno
                                    </p>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="relative group"
                        >
                            <div className="rounded-2xl overflow-hidden">
                                <img
                                    src="/dojo_instituto.webp"
                                    alt="Dojo para entrenamiento físico"
                                    className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <h3 className="text-xl font-bold text-white mb-2">Dojo de Entrenamiento</h3>
                                    <p className="text-slate-300 text-sm">
                                        Espacio para módulos de Primeros Auxilios, Defensa Personal e Instrucción Física
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Requisitos */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-600 text-sm font-medium rounded-full mb-4">
                            Documentación
                        </span>
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 font-heading">
                            Requisitos de Inscripción
                        </h2>
                        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                            Documentos necesarios para iniciar tu formación como Guardia de Seguridad Privada
                        </p>
                    </motion.div>

                    {/* Tabs */}
                    <div className="max-w-3xl mx-auto">
                        <div className="flex justify-center gap-4 mb-8">
                            <button
                                onClick={() => setActiveTab('chilenos')}
                                className={`px-6 py-3 rounded-xl font-semibold transition-all ${activeTab === 'chilenos'
                                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/30'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                            >
                                🇨🇱 Chilenos
                            </button>
                            <button
                                onClick={() => setActiveTab('extranjeros')}
                                className={`px-6 py-3 rounded-xl font-semibold transition-all ${activeTab === 'extranjeros'
                                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/30'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                            >
                                🌎 Extranjeros
                            </button>
                        </div>

                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-slate-50 rounded-2xl p-8 border border-slate-100"
                        >
                            <ul className="space-y-4">
                                {(activeTab === 'chilenos' ? requisitosChilenos : requisitosExtranjeros).map((req, index) => (
                                    <li key={index} className="flex items-start gap-3">
                                        <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                                        <span className="text-slate-700">{req}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        <p className="text-center text-slate-500 mt-6 text-sm">
                            * El certificado psicológico y el informe comercial (DICOM) están incluidos en el valor del curso.
                        </p>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-20 bg-slate-900">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <span className="inline-block px-4 py-1.5 bg-blue-500/20 text-blue-400 text-sm font-medium rounded-full mb-4">
                            Dudas Frecuentes
                        </span>
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">
                            Preguntas{' '}
                            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                                Frecuentes
                            </span>
                        </h2>
                    </motion.div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {faqs.map((faq, index) => (
                            <FAQItem key={index} {...faq} />
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Final */}
            <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <GraduationCap className="w-16 h-16 text-white/80 mx-auto mb-6" />
                        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 font-heading">
                            Comienza tu Formación Hoy
                        </h2>
                        <p className="text-xl text-blue-100 mb-8">
                            Inscríbete ahora y da el primer paso hacia tu nueva carrera en seguridad privada.
                        </p>

                        <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                                <p className="text-blue-100 text-sm mb-1">Inversión</p>
                                <p className="text-4xl font-bold text-white">$110.000 <span className="text-lg text-blue-200">CLP</span></p>
                            </div>
                            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                                <motion.button
                                    whileHover={{ scale: 1.05, y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="inline-flex items-center gap-2 px-10 py-5 bg-white text-blue-600 font-bold rounded-xl shadow-lg hover:bg-blue-50 transition-all text-lg"
                                >
                                    Inscribirme Ahora
                                    <ArrowRight className="w-6 h-6" />
                                </motion.button>
                            </a>
                        </div>

                        <div className="flex flex-wrap justify-center gap-6 mt-10 text-blue-100">
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5" />
                                <span>2 semanas de formación</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Laptop className="w-5 h-5" />
                                <span>Material online incluido</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Award className="w-5 h-5" />
                                <span>Credencial oficial SPD</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
